import complexOptionStyles from './select-complex-option-body.module.scss';
import React, { useEffect } from 'react';
import { EntityDropdownSelect } from 'ui-components/entity-dropdown/entity-dropdown-select';
import { useSetOverlayPOI } from 'features/explore/context/use-explore-ui-state-actions';
import { useGetActiveOverlay } from 'features/explore/hooks/use-explore-overlay-poi';
import { RegularOptionBody } from 'features/explore/components/explore-side-bar/components/explore-drawer/explore-drawer-option/option-body/regular-option-body';
import { PointOfInterestFilters } from 'core/entities/point-of-interest-entities';
import { PlaceOverlay } from 'ui-components/google-map-layers/types/google-map-layers-types';
import { getPlaceOverlay } from 'ui-components/google-map-layers/utils/get-place-overlay';
import { getFeatureFlags } from 'core/flow-control';
import { Dropdown, MultilineDropdownMenu, UnifiedSelectBox } from '@placer-ui/components';
import { Complex } from '@placer-ui/types';
import { formatFullAddress } from 'utils/address-formatter/address-formatter';

type MultiselectOptionProps = {
    poi: PointOfInterestFilters;
    enableSelection?: boolean;
    complexes: Complex[];
};

export const SelectComplexOptionBody = ({
    poi,
    complexes,
    enableSelection,
}: MultiselectOptionProps) => {
    const setOverlayPOI = useSetOverlayPOI();
    const getActiveOverlay = useGetActiveOverlay();
    const { accesibility_enable_explore_property_reports_ff } = getFeatureFlags();

    const overlayPoi: PlaceOverlay = {
        ...poi,
        overlayID: 'complex',
    };
    const selectedComplex = getActiveOverlay(overlayPoi);

    // if there was no overlay selected, select the first complex
    useEffect(() => {
        !selectedComplex &&
            setOverlayPOI({
                sourcePoi: poi.id,
                overlayPoi: {
                    ...complexes[0],
                    overlayID: 'complex',
                },
            });
    }, [complexes, poi, selectedComplex, setOverlayPOI]);

    if (!selectedComplex) return null;

    const onEntityDropdownChange = (complex: Complex) => {
        setOverlayPOI({
            sourcePoi: poi.id,
            overlayPoi: getPlaceOverlay(complex, 'complex'),
        });
    };

    const complexSelection = accesibility_enable_explore_property_reports_ff ? (
        <Dropdown
            scaleToAnchor
            trigger={<UnifiedSelectBox size="small" value={selectedComplex.name} data-testid="complex-dropdown-trigger" />}
            closeOnSelection
            appendToParent
            testId='enclosing-selection'
        >
            <MultilineDropdownMenu
                onItemSelection={({ value }) =>
                    onEntityDropdownChange(complexes.find(({ id }) => id === value)!)
                }
                items={complexes.map((complex) => ({
                    value: complex.id,
                    isSelected: selectedComplex.id === complex.id,
                    label: complex.name,
                    description: formatFullAddress(complex.type, complex),
                    'data-testid': `complex-dropdown-item-${complex.id}`,
                }))}
            />
        </Dropdown>
    ) : (
        <div data-testid='deprecatedenclosing-selection' className={complexOptionStyles.dropdown}>
            <EntityDropdownSelect
                options={complexes || complexes[0]}
                onChange={onEntityDropdownChange}
                value={selectedComplex}
            />
        </div>
    );

    // return the selected complex
    return (
        <>
            {complexSelection}
            <RegularOptionBody poi={overlayPoi} enableSelection={enableSelection} />
        </>
    );
};
